import { useProposal } from '@/features/proposals/hooks/use-proposal.hook';
import { Page } from '@/shared/ui/page';
import { Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useCapitalGlobalDocusignSignatureStatus } from '@/features/proposals/hooks/capital-global/use-capital-global-docusign-signature.hook';
import { useCapitalGlobalProposalSignatureMethod } from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-signature-method.hook';
import { isCapitalGlobalProposalEditable } from './utils/capital-global-proposal-editability.util';
import { isCapitalGlobalProposalProduct } from './capital-global-proposal-product.util';
import { NewCapitalGlobalProposalLayout } from './new-capital-global-proposal-layout.component';

type Props = {
  policyId: string;
  mode: 'edit' | 'view';
};

/**
 * Página dedicada à proposta Capital Global (rota `/proposals/:id/capital-global/...`).
 * Mantém o fluxo CG fora de `new-proposal-page` (legado).
 */
export function CapitalGlobalProposalPage({ policyId, mode }: Props) {
  const navigate = useNavigate();
  const { data: proposal, isLoading } = useProposal(policyId);
  const signatureQuery = useCapitalGlobalDocusignSignatureStatus(policyId);
  const signatureMethodQuery = useCapitalGlobalProposalSignatureMethod(policyId);

  useEffect(() => {
    if (isLoading || !proposal?.productCode) return;
    if (!isCapitalGlobalProposalProduct(proposal.productCode)) {
      navigate({
        to: mode === 'edit' ? '/proposals/$id/edit' : '/proposals/$id',
        params: { id: policyId },
        replace: true,
      });
    }
  }, [
    isLoading,
    proposal?.productCode,
    policyId,
    mode,
    navigate,
  ]);

  useEffect(() => {
    if (mode !== 'edit' || isLoading || !proposal) return;

    const editable = isCapitalGlobalProposalEditable({
      proposal,
      signature: signatureQuery.data ?? null,
      signatureMethod: signatureMethodQuery.data ?? null,
    });

    if (!editable) {
      navigate({
        to: '/proposals/$id/capital-global',
        params: { id: policyId },
        replace: true,
      });
    }
  }, [
    mode,
    isLoading,
    proposal,
    policyId,
    navigate,
    signatureQuery.data,
    signatureMethodQuery.data,
  ]);

  if (policyId && isLoading && !proposal) {
    return (
      <Page.Root>
        <Page.Content className='py-4'>
          <div className='flex items-center justify-center py-16'>
            <div className='flex items-center gap-3'>
              <Spinner size='sm' colorScheme='gray' />
              <Text className='text-sm text-alba-gray-600'>
                Carregando proposta...
              </Text>
            </div>
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  if (!proposal) {
    return (
      <Page.Root>
        <Page.Content className='py-4'>
          <Text className='text-sm text-alba-gray-600'>
            Proposta não encontrada.
          </Text>
        </Page.Content>
      </Page.Root>
    );
  }

  if (!isCapitalGlobalProposalProduct(proposal.productCode)) {
    return (
      <Page.Root>
        <Page.Content className='py-4'>
          <div className='flex items-center justify-center py-16'>
            <Spinner size='sm' colorScheme='gray' />
          </div>
        </Page.Content>
      </Page.Root>
    );
  }

  return (
    <Page.Root>
      <NewCapitalGlobalProposalLayout
        mode={mode}
        policyId={policyId}
        proposal={proposal}
      />
    </Page.Root>
  );
}
